import { createRoot } from "react-dom/client";

import { ClientSwitch } from "./clientSwitch";
import { createGame } from "src/engine/api/createGame";
import { exposeForTests } from "src/engine/game/debugHook";
import { GameStore } from "src/engine/game/GameStore";
import { devResumeOn, loadDevBoard, saveDevBoard } from "src/engine/game/devResume";
import { legalMoves } from "src/engine/rules/chess/moveGen";
import { moveFrom, moveTo } from "src/engine/rules/move";
import { mintCost, RULES } from "src/engine/rules/rules";
import { sound, installUnlock } from "src/client/ai/audio/engine";
import { loadSoundPrefs } from "src/client/ai/ui/soundPrefs";
import { startAnalytics } from "src/engine/telemetry/analytics";
import { loadLiveChampions } from "src/engine/net/champions";
import { InsetProvider, KitGlobals, ThemeProvider, THEME } from "src/client/ai/ui/kit";
import { seedShelf, shelfEntries } from "src/client/ai/ui/useKept";

// Google Analytics, in production builds only. See `telemetry/analytics.ts` —
// it is a no-op on a dev server and under the e2e smokes, which is what keeps
// the property answering "how many people played" rather than "how many
// browsers were opened".
startAnalytics();

// **The stored volumes, and the listener that opens the audio context.**
//
// Both before render and neither blocking paint: `applyPrefs` stashes the
// levels until there is a graph to put them on, and `installUnlock` only
// registers two one-shot listeners — a browser refuses to make noise until a
// gesture, so this is the whole of what starts the system. Doing it here
// rather than in a component is what keeps everybody's settings from being
// silently discarded when the first sound plays before the gear is opened.
sound.applyPrefs(loadSoundPrefs());
installUnlock(sound);

// **Ask for the tuner's champions, and do not wait for them.**
//
// The board must come up whether or not the server has any: an opponent
// playing the weights this build shipped with is the ordinary case, and one
// that will not start because a fetch is slow is a fault. It resolves long
// before anybody opens a game — `profileById` reads them when a board opens,
// not at import — and if it does not, nothing was lost.
void loadLiveChampions().then((taken) => {
  if (taken > 0) console.info(`champions: ${taken} from the tuner`);
});

// **One store, one `Game`, made here and nowhere else.** Both clients read
// them (`clientSwitch.tsx`), which is what lets the switch keep the board: a
// client is a view of the game, and a view can be torn down without the game
// going with it.
const store = new GameStore();
const game = createGame(store);
exposeForTests({
  store,
  // The rules a smoke needs to play something legal rather than something
  // scripted. Dev-only, like the rest of the handle.
  rules: { legalMoves, moveFrom, moveTo, mintCost, minerRate: RULES.minerRate }
});

// **A dev refresh comes back to the board it left** (`devResume.ts`): the
// store and the AI shell's kept launch state, both seeded before the first
// render so nothing opens a fresh game over them. A room's shelf is not
// saved — the server replays a room on rejoin. `?fresh` starts clean.
if (import.meta.env.DEV && new URLSearchParams(window.location.search).has("fresh")) {
  // Once: the next refresh resumes whatever this one starts.
  const url = new URL(window.location.href);
  url.searchParams.delete("fresh");
  window.history.replaceState(null, "", url);
} else if (devResumeOn()) {
  const shell = loadDevBoard(store);
  if (shell) seedShelf(game, shell);
}
if (devResumeOn()) {
  window.addEventListener("pagehide", () => {
    const kept = shelfEntries(game);
    if (kept.mode !== "net") saveDevBoard(store, kept);
  });
}

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root mount point");
// **The theme wraps everything, and there is no `CssBaseline`.** See
// `ui/kit/theme.ts`: the page's own background, height and overflow are set in
// `index.html` for a full-window canvas, and baseline would reset them.
// `KitGlobals` is the two document rules that are actually wanted — the body's
// face and colour, and form controls inheriting them — inside the provider
// because both are read off the theme.
// **`InsetProvider` inside the theme, around everything.** Every floating
// piece of chrome registers what it covers (PLAN-UI §28.1); the provider is
// what they register *with*, and it is above `App` because the badge and the
// screens either side of a game must not tear it down and lose the rects.
createRoot(container).render(
  <ThemeProvider theme={THEME}>
    <KitGlobals />
    <InsetProvider>
      {/* A bottom-right button cycles between the AI client and the human
          one (`clientSwitch.tsx`) — for whoever may see the dev tools
          (`useDevTools`); the AI App is the default.
          The A/B badge is provided there too and rendered by the client. */}
      <ClientSwitch store={store} game={game} />
    </InsetProvider>
  </ThemeProvider>
);
